import React, { ReactNode, MouseEvent, useState } from 'react';
import styled from 'styled-components';
// --------------------------------------------------------------------------------
import { Button } from './Button';
import { config } from '../index';
import { NodeConfigProps } from './Card';
import { ContentWrapper } from './ContentWrapper';
import { AppSearch } from './AppSearch';

export interface HeaderProps {
  /* User logged in state */
  user?: any;
  /* Header logo component */
  logo?: ReactNode;
  /* Header search bar state */
  search?: boolean;
  /* Header min height */
  minHeight?: string;
  /* Header border */
  border?: string;
  /* Header border bottom */
  borderBottom?: string;
  /* Header padding */
  padding?: string;
  /* Header color */
  backgroundColor?: string;
  /* Header background color */
  background?: string;
  /* Header max width */
  maxWidth?: string;
  /* Header button  */
  buttonConfig?: NodeConfigProps;
  /* Header onHomeClick callBack */
  onHomeClick?: (event?: MouseEvent) => void;
  /* Header onLogin callBack */
  onLogin?: (event?: MouseEvent) => void;
  /* Header onSignUp callBack */
  onSignUp?: (event?: MouseEvent) => void;
  /* Header onProfileClick callBack */
  onProfileClick?: (event?: MouseEvent) => void;
  /* Header onNotificationsClick callBack */
  onNotificationsClick?: (event?: MouseEvent) => void;
  /* Header onMessageClick callBack */
  onMessageClick?: (event?: MouseEvent) => void;
  /* Header onListItemClick callBack */
  onListItemClick?: (event?: MouseEvent) => void;
  /* Header onMobileMenuClick callBack */
  onMobileMenuClick?: (event?: MouseEvent) => void;
}

interface PrivateHeaderProps {
  mobile?: boolean;
}

const StyledHeaderContainer = styled.div<HeaderProps>`
  display: flex;
  flex: 1;
  align-items: center;

  padding: ${(props) => props.padding};
  border: ${(props) => props.border};
  border-bottom: ${(props) => props.borderBottom};
  background-color: ${(props) => props.backgroundColor};
`;

const StyledHeaderLogoContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${config.spacingXXLarge};
`;

const StateActionWrapper = styled.div<PrivateHeaderProps>`
  display: flex;
  flex-direction: ${(props) => (props.mobile ? 'column' : 'row')};
  align-items: center;
  gap: ${config.spacingMedium};
`;

const StyledActions = styled.div<NodeConfigProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${config.spacingMedium};
`;

const AppSearchWrapper = styled.div<NodeConfigProps>`
  @media (max-width: ${config.breakPointXLarge}) {
    display: none;
  }
`;

const StyledActionWrapper = styled.div<NodeConfigProps>`
  display: flex;
  flex-direction: row;
  margin-left: auto;

  @media (max-width: ${config.breakPointMedium}) {
    display: none;
  }
`;

const MobileMenu = styled.div<NodeConfigProps>`
  display: flex;
  flex-direction: row;
  margin-left: auto;

  @media (min-width: ${config.breakPointMedium}) {
    display: none;
  }
`;

const MobileMenuWrapper = styled.div<NodeConfigProps>`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 99;
  width: 100%;
  height: 100vh;
  background-color: ${config.transparentMat};

  @media (min-width: ${config.breakPointMedium}) {
    display: none;
  }
`;

const MobileMenuContainer = styled.div<NodeConfigProps>`
  display: flex;
  animation: slideIn 0.8s ease-in-out;
  max-width: 400px;
  height: 100%;
  margin-left: auto;
  background-color: ${config.white};
  transition: ${config.transitionShort};

  @keyframes slideIn {
    from {
      width: 0;
    }
    to {
      width: 80%;
    }
  }
`;

const MobileActionWrapper = styled.div<NodeConfigProps>`
  padding: ${config.fontLarge};
  gap: ${config.spacingMedium};
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  align-items: flex-end;
`;

export const Header: React.FC<HeaderProps> = ({
  user,
  maxWidth = '1932px',
  logo,
  search = true,
  minHeight = '132px',
  border = 'unset',
  borderBottom = border || 'unset',
  padding = `0 ${config.spacingXXLarge}`,
  buttonConfig,
  backgroundColor = config.transparent,
  background,
  onHomeClick,
  onLogin,
  onSignUp,
  onProfileClick,
  onNotificationsClick,
  onListItemClick,
  onMessageClick,
  onMobileMenuClick,
  ...props // additional props
}) => {
  const [menu, setMenu] = useState(false);

  const mobileMenuHandler = () => {
    setMenu(!menu);
    onMobileMenuClick?.();
  };

  return (
    <header>
      <ContentWrapper
        className="dm-font"
        maxWidth={maxWidth}
        backgroundColor={background}
        minHeight={minHeight}
      >
        <StyledHeaderContainer
          border={border}
          borderBottom={borderBottom}
          padding={padding}
          backgroundColor={backgroundColor}
          // additional props
          {...props}
        >
          {logo && (
            <StyledHeaderLogoContainer>{logo}</StyledHeaderLogoContainer>
          )}

          <StyledActionWrapper>
            {search && (
              <AppSearchWrapper>
                <AppSearch type="small" />
              </AppSearchWrapper>
            )}

            <StyledActions gap={buttonConfig?.gap}>
              <Button
                label={{ string: 'Home' }}
                type="transparent"
                padding="0 15px"
                onClick={onHomeClick}
              />
              <Button
                label={{ string: 'Browse' }}
                type={'transparent'}
                padding={'0 15px'}
                dropdownOptions={[
                  {
                    title: { string: 'Option One' },
                    onClick: () => console.log('Option one'),
                  },
                  {
                    title: { string: 'Option Two' },
                    onClick: () => console.log('Option two'),
                  },
                ]}
                dropdownConfig={{
                  transform: 'translate3d(0px, 0px, 0px)',
                }}
                onClick={() => console.log('Home')}
              />
              {!user && (
                <StateActionWrapper>
                  <Button label={{ string: 'Login' }} onClick={onLogin} />
                  <Button
                    label={{ string: 'Sign up' }}
                    type="outline"
                    onClick={onSignUp}
                  />
                </StateActionWrapper>
              )}
              {user && (
                <StateActionWrapper>
                  <Button
                    label={{ string: 'List an item' }}
                    type="transparent"
                    padding="0 15px"
                    onClick={onListItemClick}
                  />
                  <Button
                    icon={'bell'}
                    type={'transparent'}
                    padding={'5px'}
                    iconConfig={{
                      hoverStroke: config.red,
                    }}
                    onClick={onNotificationsClick}
                  />
                  <Button
                    icon={'message'}
                    type={'transparent'}
                    padding={'5px'}
                    iconConfig={{
                      size: 40,
                      hoverStroke: config.red,
                    }}
                    onClick={onMessageClick}
                  />
                  <Button
                    icon={'avatar'}
                    type={'transparent'}
                    padding={'5px'}
                    iconConfig={{
                      size: 40,
                      hoverStroke: config.grey,
                      hoverFill: config.grey,
                    }}
                    onClick={onProfileClick}
                  />
                </StateActionWrapper>
              )}
            </StyledActions>
          </StyledActionWrapper>

          {menu && (
            <MobileMenuWrapper>
              <MobileMenuContainer>
                <MobileActionWrapper>
                  <Button
                    icon={'close'}
                    type={'transparent'}
                    padding={'5px'}
                    iconConfig={{
                      size: 40,
                      hoverStroke: config.red,
                    }}
                    onClick={mobileMenuHandler}
                  />
                  <Button
                    label={{ string: 'Browse' }}
                    type={'transparent'}
                    padding={'0 15px'}
                    dropdownOptions={[
                      {
                        title: { string: 'Option One' },
                        onClick: () => console.log('Option one'),
                      },
                      {
                        title: { string: 'Option Two' },
                        onClick: () => console.log('Option two'),
                      },
                    ]}
                    dropdownConfig={{
                      transform: 'translate3d(0px, 0px, 0px)',
                    }}
                    onClick={() => console.log('Home')}
                  />
                  {!user && (
                    <StateActionWrapper mobile={true}>
                      <Button label={{ string: 'Login' }} onClick={onLogin} />
                      <Button
                        label={{ string: 'Sign up' }}
                        type="outline"
                        onClick={onSignUp}
                      />
                    </StateActionWrapper>
                  )}
                  {user && (
                    <StateActionWrapper mobile={true}>
                      <Button
                        label={{ string: 'List an item' }}
                        type="transparent"
                        padding="0 15px"
                        onClick={onListItemClick}
                      />
                      <Button
                        icon={'bell'}
                        type={'transparent'}
                        padding={'5px'}
                        iconConfig={{
                          hoverStroke: config.red,
                        }}
                        onClick={onNotificationsClick}
                      />
                      <Button
                        icon={'message'}
                        type={'transparent'}
                        padding={'5px'}
                        iconConfig={{
                          size: 40,
                          hoverStroke: config.red,
                        }}
                        onClick={onMessageClick}
                      />
                      <Button
                        icon={'avatar'}
                        type={'transparent'}
                        padding={'5px'}
                        iconConfig={{
                          size: 40,
                          hoverStroke: config.grey,
                          hoverFill: config.grey,
                        }}
                        onClick={onProfileClick}
                      />
                    </StateActionWrapper>
                  )}
                </MobileActionWrapper>
              </MobileMenuContainer>
            </MobileMenuWrapper>
          )}

          <MobileMenu>
            <Button
              icon={'hamburger'}
              type={'transparent'}
              padding={'5px'}
              iconConfig={{
                size: 40,
                hoverStroke: config.red,
              }}
              onClick={mobileMenuHandler}
            />
          </MobileMenu>
        </StyledHeaderContainer>
      </ContentWrapper>
    </header>
  );
};
